import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { MobileSheet } from "@/components/ui/mobile-sheet";
import { SheetDescription, SheetTitle } from "@/components/ui/sheet";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useToast, useToastError } from "@/components/ui/use-toast";
import { useApi } from "@/hooks/useApi";
import {
  ConnectedCrmIntegration,
  useConnectedCrmIntegrations,
} from "@/hooks/useConnectedCrmIntegrations";
import { useCrmRecordPresence } from "@/hooks/useCrmRecordPresence";
import { useDeviceFormat } from "@/hooks/useDeviceFormat";
import { useSeliarisOwnerChoice } from "@/hooks/useSeliarisOwnerChoice";
import { useTranslation } from "@/hooks/useTranslation";
import {
  getSeliarisErrorFields,
  getSeliarisErrorMessageKey,
} from "@/lib/seliaris-errors";
import { cn } from "@/lib/utils";
import {
  CompanyCrmIntegration,
  CompanyDTO,
  CrmIntegrationProviderSupported,
  SyncStatusCrmIntegrationDTO,
} from "@shaarp/common";
import { ExternalLink } from "lucide-react";
import { useEffect, useState } from "react";
import { MdInsertLink } from "react-icons/md";
import { Badge } from "@/components/ui/badge";
import { ConnectedCrmLogos } from "@/components/integrations/connected-crm-logos";
import {
  IntegrationDropdownContent,
  IntegrationSyncSections,
} from "@/components/integrations/company/integration-dropdown-content";

export type SyncCompany = {
  id: string;
  directLink: string;
};

/**
 * One connected CRM and what this company's state is in it.
 *
 * The record and the possible matches live here, per integration, rather than in
 * a single shared slot: with two CRMs connected, "the external id" and "the
 * candidates" are only meaningful alongside the provider they came from.
 */
export type CompanyIntegrationStatus = ConnectedCrmIntegration & {
  isSynced: boolean;
  isLoading: boolean;
  /**
   * The candidate currently being linked, when the send targets one — only that
   * row spins, the other candidates of the same CRM just wait.
   */
  loadingMatchId: string | null;
  syncEndpoint: string;
  crmRecord: SyncCompany | null;
  possibleMatches: CompanyCrmIntegration[];
};

/**
 * Summary shared with callers that persist it across the page's several copies
 * of this control. It stays deliberately provider-agnostic: `isSynced` means
 * "this company reached at least one CRM", which is all the trigger button and
 * the surrounding layout ever needed to know.
 */
export type CompanyIntegrationsSyncState = {
  isSynced: boolean;
  crmCompany: SyncCompany | null;
  possiblesMatchCrmCompanies: CompanyCrmIntegration[];
};

function buildStatuses(
  company: CompanyDTO,
  connected: ConnectedCrmIntegration[],
): CompanyIntegrationStatus[] {
  return connected.map((integration) => {
    const status = company?.integrations?.[integration.key];
    return {
      ...integration,
      isSynced: !!status?.isSynced,
      isLoading: false,
      loadingMatchId: null,
      syncEndpoint: `/company/${company.id}/integration/crm/${integration.key}`,
      crmRecord:
        status?.externalId && status?.directLink
          ? { id: status.externalId, directLink: status.directLink }
          : null,
      possibleMatches: status?.possibleMatches ?? [],
    };
  });
}

export default function CompanyIntegrationsSync({
  company,
  syncState,
  onSyncStateChange,
  onActionComplete,
  triggerClassName,
  triggerLabel,
  defaultOpen = false,
}: {
  company: CompanyDTO;
  syncState?: CompanyIntegrationsSyncState;
  onSyncStateChange?: (state: CompanyIntegrationsSyncState) => void;
  onActionComplete?: () => void;
  triggerClassName?: string;
  triggerLabel?: string;
  /** Opens the menu as it mounts: the click that asked for it saved the company first. */
  defaultOpen?: boolean;
}) {
  const api = useApi();
  const { toast } = useToast();
  const toastError = useToastError();
  const { t } = useTranslation();
  const { isMobile } = useDeviceFormat();
  const connectedIntegrations = useConnectedCrmIntegrations();
  const { sendWithOwnerChoice, ownerDialog } = useSeliarisOwnerChoice();

  const [open, setOpen] = useState(defaultOpen);

  const [integrations, setIntegrations] = useState<CompanyIntegrationStatus[]>(
    () => buildStatuses(company, connectedIntegrations),
  );

  const [localSyncState, setLocalSyncState] =
    useState<CompanyIntegrationsSyncState>({
      isSynced: false,
      crmCompany: null,
      possiblesMatchCrmCompanies: [],
    });

  // Controlled by the page when several copies of this control share one
  // company, and self-contained otherwise.
  const isControlled = syncState !== undefined;
  const { isSynced, crmCompany, possiblesMatchCrmCompanies } = isControlled
    ? syncState
    : localSyncState;

  const updateSyncState = (updates: Partial<CompanyIntegrationsSyncState>) => {
    const next = { ...(isControlled ? syncState : localSyncState), ...updates };
    if (isControlled) onSyncStateChange?.(next);
    else setLocalSyncState(next);
  };

  // Seeds the shared summary from the company DTO once, on mount, and keeps the
  // per-integration rows in step when the set of connected CRMs changes.
  useEffect(() => {
    const statuses = buildStatuses(company, connectedIntegrations);
    setIntegrations(statuses);

    const firstSynced = statuses.find((status) => status.isSynced);
    if (!firstSynced || crmCompany) return;

    const matches = statuses.flatMap((status) => status.possibleMatches);
    updateSyncState({
      isSynced: true,
      crmCompany: firstSynced.crmRecord ?? crmCompany,
      possiblesMatchCrmCompanies:
        matches.length > 0 ? matches : possiblesMatchCrmCompanies,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company.id, connectedIntegrations]);

  /**
   * Marks a single CRM as busy. Each integration carries its own flag: sending
   * a company to one CRM must leave the row of the other one untouched.
   */
  const setIntegrationLoading = (
    integrationKey: CrmIntegrationProviderSupported,
    isLoading: boolean,
    loadingMatchId: string | null = null,
  ) => {
    setIntegrations((prev) =>
      prev.map((integration) =>
        integration.key === integrationKey
          ? {
              ...integration,
              isLoading,
              loadingMatchId: isLoading ? loadingMatchId : null,
            }
          : integration,
      ),
    );
  };

  const applySyncResult = (
    integrationKey: CrmIntegrationProviderSupported,
    data: SyncStatusCrmIntegrationDTO<CompanyCrmIntegration> | undefined,
    clearMatches: boolean,
  ) => {
    const record: SyncCompany = {
      id: data?.externalId || "",
      directLink: data?.directLink || "",
    };

    setIntegrations((prev) =>
      prev.map((integration) =>
        integration.key === integrationKey
          ? {
              ...integration,
              isSynced: true,
              crmRecord: record,
              possibleMatches: [],
            }
          : integration,
      ),
    );

    updateSyncState({
      isSynced: true,
      crmCompany: data ? record : crmCompany,
      ...(clearMatches ? { possiblesMatchCrmCompanies: [] } : {}),
    });
  };

  /**
   * Shows the company as not sent to the CRMs that deleted its record, so the
   * menu offers to send it again — and drops the trigger's linked badge once
   * no CRM holds it any more.
   */
  const forgetDeletedRecords = (
    deletedIn: readonly CrmIntegrationProviderSupported[],
  ) => {
    const next = integrations.map((integration) =>
      deletedIn.includes(integration.key)
        ? {
            ...integration,
            isSynced: false,
            crmRecord: null,
            possibleMatches: [],
          }
        : integration,
    );
    setIntegrations(next);

    const stillSynced = next.find((integration) => integration.isSynced);
    updateSyncState({
      isSynced: !!stillSynced,
      crmCompany: stillSynced?.crmRecord ?? null,
    });
  };

  const { cancel: cancelRecordCheck } = useCrmRecordPresence({
    path: `/company/${company.id}/integration/crm`,
    isOpen: open,
    hasLinkedRecord: integrations.some((integration) => integration.isSynced),
    onDeleted: forgetDeletedRecords,
  });

  /**
   * Names what the CRM refused when it said so, and falls back to the generic
   * toast otherwise — a company Seliaris turns down for a missing name, or an
   * account its own CRM integration has made read-only, are both actionable
   * once said out loud.
   */
  const reportSyncError = (error: unknown, integrationName: string) => {
    const messageKey = getSeliarisErrorMessageKey(error);
    if (!messageKey) {
      toastError();
      return;
    }
    toast({
      variant: "destructive",
      title: t("company.integrations.syncErrorTitle", {
        integration: integrationName,
      }),
      // The custom fields a refusal named are interpolated when it carried
      // any; every other message ignores the parameter.
      description: t(messageKey, { fields: getSeliarisErrorFields(error) }),
    });
  };

  const notifySynced = (integrationName: string) => {
    toast({
      variant: "default",
      title: t("company.integrations.syncTitle", {
        integration: integrationName,
      }),
      description: t("company.integrations.syncSuccess", {
        integration: integrationName,
      }),
    });
  };

  /**
   * Creates or refreshes this company in the given CRM, or links it to the
   * existing record the user picked.
   *
   * Picking a record also clears the candidate list: the choice is made, and
   * leaving the others up would invite a second, contradictory link.
   *
   * When Seliaris knows no user with the sender's email, the menu closes and
   * the sender is asked whether to send the company without an owner; a
   * cancel sends no second request.
   */
  const handleSyncToIntegration = async (
    integrationKey: CrmIntegrationProviderSupported,
    externalCompanyId?: string,
  ) => {
    const integration = integrations.find((i) => i.key === integrationKey);
    if (!integration) return;

    cancelRecordCheck();
    setIntegrationLoading(integrationKey, true, externalCompanyId ?? null);

    try {
      const response = await sendWithOwnerChoice(
        company.name ?? "",
        (withoutOwner) => {
          const body = {
            ...(externalCompanyId ? { externalCompanyId } : {}),
            ...(withoutOwner ? { withoutOwner } : {}),
          };
          return api.post<SyncStatusCrmIntegrationDTO<CompanyCrmIntegration>>(
            integration.syncEndpoint,
            Object.keys(body).length > 0 ? body : undefined,
          );
        },
        { onAsk: () => setOpen(false) },
      );
      if (!response) return;
      const { data } = response;

      applySyncResult(integrationKey, data, !!externalCompanyId);
      notifySynced(integration.name);
      setOpen(false);
      onActionComplete?.();
    } catch (error) {
      reportSyncError(error, integration.name);
    } finally {
      setIntegrationLoading(integrationKey, false);
    }
  };

  const openCrmRecord = (record: { directLink?: string | null } | null) => {
    if (record?.directLink) window.open(record.directLink, "_blank");
  };

  // The synced record when there is one, the first candidate otherwise — the
  // side button always points at whatever the trigger is advertising.
  const externalRecord =
    isSynced && crmCompany ? crmCompany : possiblesMatchCrmCompanies[0];
  const showSideExternalButton = !triggerLabel && !!externalRecord;

  const renderTriggerButton = (onClick?: () => void) => (
    <Button
      variant="outline"
      aria-expanded={open}
      onClick={onClick}
      className={cn(
        "h-10",
        isSynced && "text-primary",
        showSideExternalButton && "pr-2 rounded-r-none border-r-0",
        triggerClassName,
      )}
    >
      {/* Every connected CRM shows its own logo, so the button says which ones
          it talks to rather than repeating the linked badge beside it. */}
      <ConnectedCrmLogos integrations={connectedIntegrations} />
      {triggerLabel && (
        <span className="text-sm">
          {isSynced ? t("company.integrations.availableInCrm") : triggerLabel}
        </span>
      )}
      {isSynced ? (
        <MdInsertLink className="shrink-0 w-4 h-4 text-success" />
      ) : possiblesMatchCrmCompanies.length > 0 ? (
        <Badge variant="outline" className="rounded-full">
          {possiblesMatchCrmCompanies.length}
        </Badge>
      ) : null}
    </Button>
  );

  const sectionsProps = {
    integrations,
    onSync: handleSyncToIntegration,
    onOpenExternal: openCrmRecord,
  };

  // Nothing to offer when the team has connected no CRM at all.
  if (connectedIntegrations.length === 0) return null;

  if (isMobile) {
    return (
      <>
        {ownerDialog}
        {renderTriggerButton(() => setOpen(true))}
        <MobileSheet open={open} onOpenChange={setOpen} heightClass="h-[80dvh]">
          <div className="px-5 pt-2 pb-3 shrink-0">
            <SheetTitle className="text-xl font-semibold tracking-tight">
              {t("company.integrations.menuTitle")}
            </SheetTitle>
            <SheetDescription className="sr-only">
              {t("company.integrations.menuTitle")}
            </SheetDescription>
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto px-2 pb-4">
            <IntegrationSyncSections {...sectionsProps} />
          </div>
        </MobileSheet>
      </>
    );
  }

  return (
    <div className="flex">
      {ownerDialog}
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild>
          {renderTriggerButton()}
        </DropdownMenuTrigger>
        <IntegrationDropdownContent {...sectionsProps} />
      </DropdownMenu>
      {showSideExternalButton && (
        <ExternalCompanyItem
          isActive={isSynced && !!crmCompany}
          onClick={() => openCrmRecord(externalRecord)}
        />
      )}
    </div>
  );
}

const ExternalCompanyItem = ({
  isActive,
  onClick,
}: {
  isActive: boolean;
  onClick: () => void;
}) => {
  const { t } = useTranslation();

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="outline"
          size="icon"
          className={cn(
            "h-10 rounded-l-none border-l-0 [&_svg]:size-3.5!",
            isActive && "text-primary",
          )}
          onClick={onClick}
        >
          <ExternalLink />
        </Button>
      </TooltipTrigger>
      <TooltipContent>
        <p>{t("company.integrations.openInCrmTooltip")}</p>
      </TooltipContent>
    </Tooltip>
  );
};
